import React, { useEffect, useState } from "react";
import { FiFilter, FiSearch, FiStar, FiHeart, FiEye, FiShoppingCart } from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { baseurl, imgurl } from "../baseurl";
import productDefault from "../assets/img/product.webp";
import Wishlist from "../components/Wishlist";

function Shop() {
  const { category, subcategory } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter states
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        let url = `${baseurl}/products/category/${category}`;
        if (subcategory) url += `/subcategory/${subcategory}`;

        const res = await axios.get(url);
        if (res.data.success) {
          setProducts(res.data.products || []);
        }
      } catch (err) {
        setError("Failed to fetch products. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, subcategory]);

  const getDiscount = (price, mrp) => {
    const p = parseFloat(price);
    const m = parseFloat(mrp);
    if (!m || m <= p) return null;
    return Math.round(((m - p) / m) * 100);
  };

  const brands = Array.from(new Set(products.map((p) => p.brand_name || "Other")));
  
  const priceRanges = [
    { label: "Under ₹1,000", min: 0, max: 1000 },
    { label: "₹1,000 - ₹5,000", min: 1000, max: 5000 },
    { label: "Above ₹10,000", min: 10000, max: 999999 },
  ];

  const toggleFilter = (value, state, setState) => {
    if (state.includes(value)) setState(state.filter((v) => v !== value));
    else setState([...state, value]);
  };

  const filteredProducts = products.filter((p) => {
    const searchMatch = p.product_name.toLowerCase().includes(searchTerm.toLowerCase());
    const brandMatch = selectedBrands.length === 0 || selectedBrands.includes(p.brand_name || "Other");
    
    let priceMatch = true;
    if (selectedPriceRange.length) {
      const price = parseFloat(p.price);
      priceMatch = selectedPriceRange.some((label) => {
        const range = priceRanges.find((r) => r.label === label);
        return price >= range.min && price <= range.max;
      });
    }
    return searchMatch && brandMatch && priceMatch;
  });

  return (
    <div className="product-section my-3">
      {/* Header Bar */}
      <div className="container-fluid px-lg-5 mb-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-3">
          <div className="section-title">
            <span className="text-uppercase tracking-wider text-muted small fw-bold">Shop Collection</span>
            <h3 className="fw-bold m-0 text-dark text-capitalize">
                {subcategory || category || "All Products"}
            </h3>
            <div className="accent-line"></div>
          </div>
          
          <div className="d-flex gap-2">
            <div className="position-relative search-container">
              <input 
                type="text" 
                className="form-control ps-5 rounded-pill border-light shadow-sm" 
                placeholder="Search products..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FiSearch className="position-absolute top-50 translate-middle-y ms-3 text-muted" />
            </div>
            <button 
              className="btn btn-dark rounded-pill d-md-none px-3"
              data-bs-toggle="offcanvas" data-bs-target="#mobileFilters"
            >
              <FiFilter />
            </button>
          </div>
        </div>
      </div>

      <div className="container-fluid px-lg-5">
        <div className="row">
          {/* Desktop Sidebar */}
          <aside className="col-md-3 col-xl-2 d-none d-md-block">
            <div className="filter-card p-3 bg-white rounded-4 border shadow-sm">
              <p className="fw-bold small text-uppercase mb-3">Filter By Brand</p>
              {brands.map(b => (
                <label key={b} className="d-flex align-items-center mb-2 small text-muted cursor-pointer">
                  <input 
                    type="checkbox" className="form-check-input me-2" 
                    onChange={() => toggleFilter(b, selectedBrands, setSelectedBrands)} 
                  /> {b}
                </label>
              ))}
              
              <hr />
              
              <p className="fw-bold small text-uppercase mb-3">Price Range</p>
              {priceRanges.map(r => (
                <label key={r.label} className="d-flex align-items-center mb-2 small text-muted cursor-pointer">
                   <input 
                    type="checkbox" className="form-check-input me-2" 
                    onChange={() => toggleFilter(r.label, selectedPriceRange, setSelectedPriceRange)} 
                   /> {r.label}
                </label>
              ))}
            </div>
          </aside>

          {/* Product Grid */}
          <div className="col-md-9 col-xl-10">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status"></div>
                <p className="mt-2 text-muted">Loading premium products...</p>
              </div>
            ) : (
              <div className="row g-2 g-md-4">
                {filteredProducts.map((p) => {
                  const discount = getDiscount(p.price, p.mrp);
                  const savings = p.mrp - p.price;

                  return (
                    <div key={p.id} className="col-6 col-md-4 col-lg-3">
                      <div className="card-inner shadow-sm h-100">
                        {discount > 0 && (
                          <div className="badge-discount">
                            <span className="discount-value">{discount}%</span>
                            <span className="discount-label">OFF</span>
                          </div>
                        )}
                        
                        <div className="card-actions">
                          <Wishlist productId={p.id} />
                          {/* <button className="action-btn" title="Quick View">
                            <FiEye />
                          </button> */}
                        </div>

                        <Link to={`/product/${p.slug}`} className="image-box">
                          <img
                            src={p.featured_image ? `${imgurl}${p.featured_image}` : productDefault}
                            alt={p.product_name}
                            onError={(e) => { e.target.src = productDefault; }}
                          />
                        </Link>

                        <div className="info-box">
                          <p className="sub-cat">{p.brand_name || 'Premium Selection'}</p>
                          <Link to={`/product/${p.slug}`} className="product-name">
                            {p.product_name}
                          </Link>
                          
                          <div className="price-container">
                            <div className="d-flex align-items-center gap-2">
                              <span className="curr-price">₹{parseInt(p.price).toLocaleString("en-IN")}</span>
                              {discount > 0 && <span className="old-price">₹{parseInt(p.mrp).toLocaleString("en-IN")}</span>}
                            </div>
                            {discount > 0 && (
                              <div className="savings-tag">Save ₹{savings.toLocaleString("en-IN")}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {!loading && filteredProducts.length === 0 && (
                <div className="text-center py-5"><h5 className="text-muted">No products found.</h5></div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Offcanvas */}
      <div className="offcanvas offcanvas-bottom rounded-top-5" style={{height: '60vh'}} id="mobileFilters">
        <div className="offcanvas-header border-bottom">
          <h6 className="offcanvas-title fw-bold">Refine Results</h6>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas"></button>
        </div>
        <div className="offcanvas-body">
            <p className="fw-bold small text-uppercase mb-2">Brands</p>
            <div className="d-flex flex-wrap gap-2 mb-4">
                {brands.map(b => (
                    <button 
                        key={b} 
                        className={`btn btn-sm rounded-pill ${selectedBrands.includes(b) ? 'btn-dark' : 'btn-outline-secondary'}`}
                        onClick={() => toggleFilter(b, selectedBrands, setSelectedBrands)}
                    >
                        {b}
                    </button>
                ))}
            </div>
            <p className="fw-bold small text-uppercase mb-2">Price Range</p>
            {priceRanges.map(r => (
                <div key={r.label} className="form-check py-2 border-bottom">
                    <input className="form-check-input" type="checkbox" onChange={() => toggleFilter(r.label, selectedPriceRange, setSelectedPriceRange)} />
                    <label className="form-check-label small">{r.label}</label>
                </div>
            ))}
        </div>
      </div>

      <style>{`
        .tracking-wider { letter-spacing: 1.5px; }
        .accent-line { width: 40px; height: 3px; background: #ff4757; margin-top: 8px; border-radius: 10px; }
        .search-container { min-width: 280px; }
        
        .card-inner {
          background: #fff; border-radius: 20px; overflow: hidden; position: relative;
          border: 1px solid rgba(214, 214, 214, 0.83); 
          transition: 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
     
        .badge-discount {
          position: absolute; top: 15px; left: 15px; z-index: 5;
          background: #ed712f; color: #fff; padding: 4px 10px;
          border-radius: 10px; display: flex; flex-direction: column; align-items: center;
          line-height: 1; box-shadow: 0 4px 10px rgba(237, 113, 47, 0.3);
        }
        .discount-value { font-size: 0.8rem; font-weight: 900; }
        .discount-label { font-size: 0.5rem; font-weight: 700; }

        .image-box { display: flex; align-items: center; justify-content: center; position: relative; height: 220px; overflow: hidden; background: #f9fafb; }
        .image-box img { width: 80%; height: 80%; object-fit: contain; transition: 0.6s ease; }
       

        .card-actions { position: absolute; top: 15px; right: -50px; display: flex; flex-direction: column; gap: 8px; transition: 0.3s ease; z-index: 10; }
        .card-inner:hover .card-actions { right: 15px; }
        
        .action-btn { 
            width: 36px; height: 36px; border-radius: 50%; background: #ffffff; border: 1px solid #eee; 
            display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 10px rgba(0,0,0,0.05);
            color: #333; transition: 0.3s;
        }
        .action-btn:hover { background: #ed712f; color: #fff; border-color: #ed712f; }

        .info-box { padding: 12px; }
        .sub-cat { font-size: 0.65rem; font-weight: 700; color: #ed712f; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
        .product-name { 
          font-size: .9rem; font-weight: 600; color: #2d3436; 
          text-decoration: none; display: -webkit-box; -webkit-line-clamp: 2; 
          -webkit-box-orient: vertical; overflow: hidden; margin-bottom: 8px;
          line-height: 1.4; height: 2.8em;
        }
        .curr-price { font-size: 1.15rem; font-weight: 900; color: #1a202c; }
        .old-price { font-size: 0.85rem; text-decoration: line-through; color: #a0aec0; }
        .savings-tag { 
          font-size: 0.65rem; color: #38a169; font-weight: 800; background: #f0fff4; 
          padding: 2px 8px; border-radius: 5px; width: fit-content; border: 1px solid #c6f6d5;
        }

        @media (max-width: 768px) {
          .image-box { height: 160px; }
          .card-actions { right: 10px; }
          .product-name { font-size: 0.8rem; }
          .search-container { min-width: 100%; }
        }
      `}</style>
    </div>
  );
}

export default Shop;